import type { AdminWorkspace, RenewalContent } from '../types'

/**
 * The Save payload for a workspace, and its inverse for React state. Lives
 * here so the workspace editor and the order form page build the same
 * payload; a field missing from workspacePatch is a field a reload loses.
 */
export function workspacePatch(workspace: AdminWorkspace): Record<string, unknown> {
  return {
    overview: workspace.overview,
    recap: workspace.recap,
    currentNextStep: workspace.currentNextStep,
    precallOnepager: workspace.precallOnepager || {},
    successContent: workspace.successContent || { summary: null, metrics: [] },
    ...(workspace.callCopyMode ? { callCopyMode: workspace.callCopyMode } : {}),
    projects: workspace.projects || [],
    renewal: workspace.renewal || {},
    orderForms: workspace.orderForms || [],
    ...(workspace.accountAddress !== undefined ? { accountAddress: workspace.accountAddress } : {}),
    phase: workspace.phase,
    pricingModel: workspace.pricingModel,
    pricingVisibility: workspace.pricingVisibility,
    pricingModes: workspace.pricingModes,
    customerSegment: workspace.customerSegment,
    status: workspace.status,
    allowedDomains: workspace.allowedDomains,
    calculatorInputs: workspace.calculatorInputs,
    businessCase: workspace.businessCase,
    tasks: workspace.tasks,
    memberRoles: workspace.members.filter((member) => /^[0-9a-f-]{36}$/i.test(member.id)).map((member) => ({ id: member.id, stakeholderRole: member.stakeholderRole || null, name: member.name.trim(), title: member.title ? member.title.trim() : null })),
    assetVisibilities: workspace.assets.filter((asset) => /^[0-9a-f-]{36}$/i.test(asset.id)).map((asset) => ({ id: asset.id, visibility: asset.visibility || 'pending' })),
  }
}

/**
 * The proposal (renewal) with safe defaults, or null when there is none. The
 * Save payload uses an empty object for "no proposal"; React state and every
 * reader expect null or a complete record, so a partial one can never crash
 * a page that maps over highlights or options.
 */
export function normalizeRenewal(raw: unknown): RenewalContent | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const value = raw as Partial<RenewalContent>
  if (Object.keys(value).length === 0) return null
  return {
    visibility: value.visibility === 'published' ? 'published' : 'draft',
    headline: typeof value.headline === 'string' ? value.headline : '',
    summary: typeof value.summary === 'string' ? value.summary : '',
    current: value.current && typeof value.current === 'object' ? value.current : null,
    highlights: Array.isArray(value.highlights) ? value.highlights.filter((item): item is string => typeof item === 'string') : [],
    options: Array.isArray(value.options) ? value.options : [],
  }
}

export function workspaceStatePatch(patch: Record<string, unknown>): Record<string, unknown> {
  const statePatch = { ...patch }
  // Same for the proposal: the API's empty object means "none". Feeding it
  // into state made the editor render a proposal with no highlights and
  // crash on "New Order Form" in a workspace without a proposal.
  if ('renewal' in statePatch) statePatch.renewal = normalizeRenewal(statePatch.renewal)
  // The API uses an empty object for an unconfigured one-pager, while the
  // editor DTO uses null. Do not feed the persistence sentinel into React.
  if (statePatch.precallOnepager && typeof statePatch.precallOnepager === 'object' && Object.keys(statePatch.precallOnepager).length === 0) {
    statePatch.precallOnepager = null
  }
  delete statePatch.memberRoles
  delete statePatch.assetVisibilities
  return statePatch
}
